import { bbSave, bbSnapshot, bbAfterSubmit, bbIsRunning } from '/bb/index.js';
import { SUBMIT_DEVICE_WAIT_MS } from '/fp/device-cache.mjs';
import { SDK_ERROR, SDK_WARNING, errorCodeFrom } from '/errors.js';
import { invokeCallback } from '/utils.js';
import { getBbDebugState } from './debug.js';
import { getBbIntegrationState, recordBbCollectResult } from './integration-state.js';

const BB_COLLECT_EVENT_NAME = 'riskseal:bb:collect';

/** @type {(() => Promise<{ session_id: string | null, error?: string }>) | null} */
let performCollectImpl = null;
/** @type {(() => number) | null} */
let holdExtraImpl = null;

export function bbHoldExtraMs() {
  return typeof holdExtraImpl === 'function' ? holdExtraImpl() : 0;
}

/**
 * Send the current BB buffer. Used by auto-save, which must not go through the public command:
 * `rsSDK('bb:collect')` is ignored while `bb.auto` is true. In `mode: 'default'` it resolves with no value as
 * soon as the submit request is out, or with the submit result if that comes first.
 *
 * @returns {Promise<{ session_id: string | null, error?: string } | undefined>}
 */
export function performBbCollect() {
  if (typeof performCollectImpl !== 'function') {
    return Promise.resolve({ session_id: null, error: SDK_ERROR.BB_DISABLED });
  }
  return performCollectImpl();
}

/**
 * Build the bb command handlers used by `rsSDK('bb:collect', ...)` and `rsSDK('bb:debug')`.
 *
 * @param {{ getAppId: () => string, getOptions: () => object, getSubmit?: () => ((opts?: { onDispatched?: () => void }) => Promise<object>) | null }} api
 * @returns {{ collect: Function, debug: Function, snapshot: Function, afterSubmit: Function, holdSubmitTimeoutMs: () => number }}
 */
export function createBbCommands({ getAppId, getOptions, getSubmit }) {
  let _bbCollectInFlight = null;

  function submitFn() {
    return typeof getSubmit === 'function' ? getSubmit() : null;
  }

  function submitAsBbCollect(callback, submit, opts) {
    return Promise.resolve(submit(opts)).then(
      function (res) {
        if (res && res.warning === SDK_WARNING.NO_BEHAVIOUR_DATA) return reportBbCollectResult(callback, res);
        return resolveBbCollectResult(callback, res);
      },
      function (err) {
        return resolveBbCollectResult(callback, {
          session_id: null,
          error: errorCodeFrom(err, SDK_ERROR.SUBMIT_FAILED),
        });
      }
    );
  }

  function runBbCollect(callback) {
    const state = getBbIntegrationState();
    if (!state.enabled) {
      return resolveCollect(callback, { session_id: null, error: SDK_ERROR.BB_DISABLED });
    }
    if (state.auto) {
      return resolveCollect(callback, { session_id: null, error: SDK_ERROR.BB_AUTO });
    }
    const submit = submitFn();
    if (!submit) return performCollect(callback);
    if (!bbIsRunning()) return resolveCollect(callback, { session_id: null, error: SDK_ERROR.BB_NOT_RUNNING });
    return submitAsBbCollect(callback, submit);
  }

  function autoSubmit(submit) {
    let markDispatched = null;
    const dispatched = new Promise(function (resolve) {
      markDispatched = resolve;
    });
    return Promise.race([dispatched, submitAsBbCollect(null, submit, { onDispatched: markDispatched })]);
  }

  function performCollect(callback) {
    const opts = getOptions();

    if (typeof window === 'undefined') {
      if (opts.debug) console.warn('[rsSDK] browser only');
      return resolveCollect(callback, { session_id: null, error: SDK_ERROR.BROWSER_ONLY });
    }

    if (!getAppId()) {
      return resolveCollect(callback, { session_id: null, error: SDK_ERROR.MISSING_CONFIG });
    }

    if (_bbCollectInFlight) {
      const existing = _bbCollectInFlight;
      if (callback) {
        existing.then(function (res) {
          invokeCallback(callback, res);
        });
      }
      return existing;
    }
    _bbCollectInFlight = bbSave(getAppId(), opts).then(
      function (res) {
        _bbCollectInFlight = null;
        return resolveBbCollectResult(callback, res);
      },
      function (err) {
        _bbCollectInFlight = null;
        const res = {
          session_id: null,
          error: errorCodeFrom(err, SDK_ERROR.BB_FAILED),
        };
        return resolveBbCollectResult(callback, res);
      }
    );
    return _bbCollectInFlight;
  }

  performCollectImpl = function () {
    const submit = submitFn();
    return submit ? autoSubmit(submit) : performCollect();
  };
  holdExtraImpl = function () {
    return submitFn() ? SUBMIT_DEVICE_WAIT_MS : 0;
  };

  return {
    collect: runBbCollect,
    debug: function () {
      return getBbDebugState({ getAppId });
    },
    snapshot: function (appId, sessionId) {
      if (!getBbIntegrationState().enabled) return null;
      return bbSnapshot(appId, sessionId);
    },
    afterSubmit: function (appId, sessionId, snapshot, result) {
      recordBbCollectResult(result);
      if (snapshot) bbAfterSubmit(appId, sessionId, snapshot, result);
    },
    holdSubmitTimeoutMs: function () {
      return getBbIntegrationState().holdSubmitTimeoutMs;
    },
  };

  function resolveCollect(callback, result) {
    recordBbCollectResult(result);
    emitBbCollectEvent(result);
    invokeCallback(callback, result);
    return Promise.resolve(result);
  }

  function resolveBbCollectResult(callback, result) {
    recordBbCollectResult(result);
    return reportBbCollectResult(callback, result);
  }

  function reportBbCollectResult(callback, result) {
    emitBbCollectEvent(result);
    invokeCallback(callback, result);
    return result;
  }

  function emitBbCollectEvent(result) {
    if (typeof window === 'undefined' || typeof window.dispatchEvent !== 'function') return;
    if (typeof CustomEvent !== 'function') return;

    try {
      const state = getBbIntegrationState();
      const detail = {
        session_id: result && typeof result.session_id === 'string' ? result.session_id : null,
        status: normalizeCollectStatus(state.lastCollectStatus),
        auto: state.auto,
      };
      if (result && result.error) detail.error = String(result.error);
      if (result && result.warning) detail.warning = String(result.warning);
      window.dispatchEvent(new CustomEvent(BB_COLLECT_EVENT_NAME, { detail }));
    } catch (e) {
      /* collect events must not affect the customer's submit flow */
    }
  }

  function normalizeCollectStatus(status) {
    if (status === 'ok' || status === 'timeout') return status;
    return 'error';
  }
}
